import { Card, Col, Row, Text, Title } from '@wyz/ui';
import * as React from 'react';
import { Trans, useLocale } from '~/common/context';
import type { IRedistributionPricingResult } from '~/purchase/types';

type Props = {
  currency: string;
  values: IRedistributionPricingResult;
};
const TotalSection: React.FC<Props> = ({ values, currency }) => {
  const locale = useLocale();

  return (
    <Card className='p-4'>
      <Row>
        <Col sm={6}>
          <Title weight='bold' className='mb-3' wrap>
            <Trans i18nKey='app.purchase.redistribution.order_accept.recap_title' />
          </Title>
        </Col>
        <Col sm={6}>
          <Row>
            <Col sm={4} className='offset-4'>
              <Text
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
                className='text-right mb-2'
              >
                <Trans i18nKey='app.purchase.redistribution.order_accept.total_price_buying' />
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
                className='text-right mb-2'
              >
                <Trans i18nKey='app.purchase.redistribution.order_accept.total_price_selling' />
              </Text>
            </Col>
          </Row>
          <Row>
            <Col sm={4}>
              <Text
                className='pb-2 pt-2'
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
              >
                <Trans i18nKey='app.purchase.redistribution.order_accept.total_price_excl_tax' />
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='semi-bold'
                color='secondary-gray'
                id={`total-excl-tax-product`}
              >
                {locale.formatters.money(values.buyingTotalExclTaxProduct, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='semi-bold'
                color='black'
                id={`total-excl-tax-product`}
              >
                {locale.formatters.money(values.sellingTotalExclTaxProduct, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
          </Row>
          {values.ecoTax !== 0 && (
            <Row>
              <Col sm={4}>
                <Text
                  className='pb-2 pt-2'
                  heading='xs'
                  weight='semi-bold'
                  color='secondary-gray'
                >
                  <Trans i18nKey='app.purchase.redistribution.order_accept.ecotax' />
                </Text>
              </Col>

              <Col sm={4}>
                <Text
                  className='p-2 text-right'
                  heading='sm'
                  weight='semi-bold'
                  color='success'
                  id={`total-excl-tax-eco-tax`}
                >
                  {locale.formatters.money(values.ecoTax, {
                    fallback: '-',
                    currency: currency,
                  })}
                </Text>
              </Col>
              <Col sm={4}>
                <Text
                  className='p-2 text-right'
                  heading='sm'
                  weight='semi-bold'
                  color='success'
                  id={`total-excl-tax-eco-tax`}
                >
                  {locale.formatters.money(values.ecoTax, {
                    fallback: '-',
                    currency: currency,
                  })}
                </Text>
              </Col>
            </Row>
          )}
          <Row>
            <Col sm={4}>
              <Text
                className='pb-2 pt-2'
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
              >
                <Trans i18nKey='app.purchase.redistribution.order_accept.delivery_excl_tax' />
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='semi-bold'
                color='secondary-gray'
                id={`total-excl-tax-delivery`}
              >
                {locale.formatters.money(values.buyingTotalExclTaxDelivery, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='semi-bold'
                color='black'
                id={`total-excl-tax-delivery`}
              >
                {locale.formatters.money(values.sellingTotalExclTaxDelivery, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col sm={4}>
              <Text
                className='pb-2 pt-2'
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
              >
                <Trans i18nKey='app.common.total_price_without_tax' />
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='bold'
                id={`total-excl-tax`}
                color='secondary-gray'
              >
                {locale.formatters.money(values.buyingTotalExclTax, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                weight='bold'
                id={`total-excl-tax`}
                color='black'
                heading='sm'
              >
                {locale.formatters.money(values.sellingTotalExclTax, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
          </Row>
          <Row>
            <Col sm={4}>
              <Text
                className='pb-2 pt-2'
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
              >
                <Trans
                  i18nKey='app.common.vat'
                  values={{
                    fallback: '-',
                    currency: currency,
                  }}
                />
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='semi-bold'
                id={`total-excl-tax`}
                color='secondary-gray'
              >
                {locale.formatters.money(values.buyingTaxAmount, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='semi-bold'
                id={`total-excl-tax`}
                color='black'
              >
                {locale.formatters.money(values.sellingTaxAmount, {
                  fallback: '-',

                  currency: currency,
                })}
              </Text>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col sm={4}>
              <Text
                className='pb-2 pt-2'
                heading='xs'
                weight='semi-bold'
                color='secondary-gray'
              >
                <Trans i18nKey='app.common.total_price_with_tax' />
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='bold'
                id={`total-excl-tax`}
                color='secondary-gray'
              >
                {locale.formatters.money(values.buyingTotalInclTax, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
            <Col sm={4}>
              <Text
                className='p-2 text-right'
                heading='sm'
                weight='bold'
                id={`total-excl-tax`}
                color='black'
              >
                {locale.formatters.money(values.sellingTotalInclTax, {
                  fallback: '-',
                  currency: currency,
                })}
              </Text>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default TotalSection;
